import { makeStyles, TextField } from '@material-ui/core';
import { Grid, Typography, CircularProgress } from '@material-ui/core/';
import { withStyles } from '@material-ui/core/styles';
import React, { useEffect, useState } from 'react';
import {
  Button,
  Edit,
  FormTab,
  TabbedForm,
  TopToolbar,
  List
} from 'react-admin';
import { FormSpy } from 'react-final-form';
import { connect } from 'react-redux';
import omit from 'lodash/omit';
import ButtonMUI from '@material-ui/core/Button';
import Empty from '../../core/components/list/Empty';
import EditToolbar from '../../core/components/edit/EditToolbar';
import LeaveConfirm from '../../core/components/LeaveConfirmWrapper';
import FormTitleBar from '../../core/components/FormTitleBar';
import { ListPagination } from '../../core/components/list/ListPagination';
import NavigationPrompt from '../../core/components/NavigationPrompt';
import {
  getInitials,
  predictiveSearchFormatter,
  predictiveSearchParser,
  removePathEnd
} from '../../core/utils';
import AddExistingInvestorDialog from './organizationsinvestor/AddExistingInvestorDialog';
import OrganizationInvestorCreateDialog from './organizationsinvestor/OrganizationInvestorCreateDialog';
import OrganizationInvestorDatagrid from './organizationsinvestor/OrganizationInvestorDatagrid';
import RemoveOrganizationInvestorButton from './organizationsinvestor/RemoveOrganizationInvestorButton';
import OrganizationSummary from './OrganizationSummary';
import ViewTab from '../../core/components/forked/layout/ViewTab';
import { ExcelIcon } from '../../core/themes/icons/ExcelIcon';
import OrganizationApi from '../../core/apis/OrganizationApi';
import { saveAs } from 'file-saver';

const styles = () => ({
  noActions: {
    marginTop: '0px'
  }
});

const useStyles = makeStyles({
  headerContainer: {
    paddingTop: '8px'
  }
});

const InvestorActionStyles = {
  addButton: {
    border: '1px solid #E0E0E0',
    background: '#FFFFFF',
    marginLeft: '10px'
  },
  ButtonMUISpan: {
    paddingLeft: '4px'
  },
  circularProgress: {
    paddingRight: '3px'
  }
};

const useFilterStyles = makeStyles({
  filterInput: {
    width: 'unset',
    alignSelf: 'center'
  }
});

const ExcelIconImage = withStyles(styles)(({ classes, icon }) => {
  return <ExcelIcon className={classes.excelIcon}>{icon}</ExcelIcon>;
});

const InvestorFilters = ({ setFilters, filterValues }) => {
  const [investorNameFilter, setInvestorNameFilter] = useState('');
  const classes = useFilterStyles();

  const handleNameFilterChange = event => {
    setInvestorNameFilter(event.target.value);
  };

  useEffect(() => {
    setFilters({
      ...filterValues,
      name: predictiveSearchParser(investorNameFilter)
    });
  }, [filterValues, investorNameFilter, setFilters]);

  return (
    <TextField
      className={classes.filterInput}
      onChange={handleNameFilterChange}
      label="Search by Investor Name"
      variant="standard"
      value={predictiveSearchFormatter(investorNameFilter)}
    />
  );
};

const OrganizationInvestorAction = withStyles(InvestorActionStyles)(
  ({
    classes,
    hasCreateInvestorPermission,
    hasEditInvestorPermission,
    hasExportOrganizationRelatedInvestorPermission,
    ...props
  }) => {
    const [submitting, setSubmitting] = useState(false);

    const handleClickDownloadAsExcel = async (investorFamilyId, filters) => {
      setSubmitting(true);
      OrganizationApi.getRelatedContactsAsExcel(investorFamilyId, filters)
        .then(resp => {
          const fileName = 'Investor Family Related Investors.xlsx';
          saveAs(resp, fileName);
        })
        .finally(() => {
          setSubmitting(false);
        });
    };
    return (
      <TopToolbar>
        {hasExportOrganizationRelatedInvestorPermission && (
          <ButtonMUI
            aria-controls="simple-menu"
            aria-haspopup="true"
            disabled={submitting}
            onClick={() =>
              handleClickDownloadAsExcel(
                props.organizationId,
                props.filterValues
              )
            }
          >
            <div className={classes.WrapperClass}>
              {submitting && (
                <span className={classes.circularProgress}>
                  <CircularProgress size={'15px'} />
                </span>
              )}
              <ExcelIconImage />
              <span className={classes.ButtonMUISpan}>
                Export Investors to Excel
              </span>
            </div>
          </ButtonMUI>
        )}
        {hasCreateInvestorPermission && (
          <Button
            color="primary"
            onClick={props.handleInvestorCreateButtonClick}
            label="Add New Investor"
            className={classes.addButton}
            disableFocusRipple
          />
        )}
        {hasEditInvestorPermission && (
          <Button
            color="primary"
            onClick={props.handleAddExistingInvestorCreateDialogToggle}
            label="Add Existing Investor"
            className={classes.addButton}
            disableFocusRipple
          />
        )}
      </TopToolbar>
    );
  }
);

const OrganizationTitle = ({ record }) => {
  return (
    <FormTitleBar
      recordLabel={'Investor Family ID: ' + record.id}
      name={record.name}
      status={record.status}
      statusLabel={'Status:'}
      showAvatar={true}
      avatarContent={getInitials(record.name)}
    />
  );
};

const OrganizationRelatedInvestorsLabel = ({ label }) => {
  const classes = useStyles();

  return (
    <Grid container>
      <Grid item xs={12}>
        <Typography variant="h5" className={classes.headerContainer}>
          {label}
        </Typography>
      </Grid>
    </Grid>
  );
};

class OrganizationEdit extends React.Component {
  state = {
    showOrganizationInvestorCreateDialog: false,
    showAddExistingInvestorCreateDialog: false,
    whenBlockNavigation: false
  };

  handleOrganizationInvestorCreateDialogToggle = () => {
    this.setState(prevState => {
      return {
        showOrganizationInvestorCreateDialog: !prevState.showOrganizationInvestorCreateDialog
      };
    });
  };

  handleAddExistingInvestorCreateDialogToggle = () => {
    this.setState(prevState => {
      return {
        showAddExistingInvestorCreateDialog: !prevState.showAddExistingInvestorCreateDialog
      };
    });
  };

  onBeforeDelete = () => {
    // Unblock navigation before deleting
    this.setState({
      whenBlockNavigation: false
    });
  };

  handleFormIsDirty(dirty) {
    // Block navigation if the form is dirty
    this.setState({ whenBlockNavigation: dirty });
  }

  render() {
    const {
      showOrganizationInvestorCreateDialog,
      showAddExistingInvestorCreateDialog,
      whenBlockNavigation
    } = this.state;
    const organizationId = this.props.id;
    const displayToolbar = this.props.match.isExact;
    const {
      classes,
      dispatch,
      permissions = [],
      hasCreatePermission,
      hasEditPermission,
      hasDeletePermission,
      ...rest
    } = this.props;
    const hasCreateInvestorPermission = permissions.includes(
      'AUTH_CREATE_INVESTOR'
    );
    const hasEditInvestorPermission = permissions.includes(
      'AUTH_EDIT_INVESTOR'
    );
    const hasExportOrganizationRelatedInvestorPermission = permissions.includes(
      'AUTH_EXPORT_INVESTOR_FAMILY_RELATED_INVESTORS'
    );

    return (
      <React.Fragment>
        <NavigationPrompt
          when={whenBlockNavigation}
          adjustMatchingPath={removePathEnd}
          hasEditPermission={hasEditPermission}
        >
          {(isOpen, onConfirm, onCancel) => (
            <LeaveConfirm
              isOpen={isOpen}
              onConfirm={onConfirm}
              onClose={onCancel}
            />
          )}
        </NavigationPrompt>

        <Edit
          {...omit(rest, ['userProfile'])}
          undoable={false}
          title={<OrganizationTitle />}
        >
          <TabbedForm
            toolbar={
              displayToolbar ? (
                <EditToolbar
                  {...rest}
                  hasEditPermission={hasEditPermission}
                  hasDeletePermission={hasDeletePermission}
                  onBeforeDelete={this.onBeforeDelete}
                />
              ) : (
                <></>
              )
            }
            variant="standard"
            margin="normal"
          >
            <FormTab label="Investor Family Details">
              <OrganizationSummary creation={false} />
              <FormSpy
                subscription={{ dirty: true }}
                onChange={props => {
                  const self = this;
                  setTimeout(() => self.handleFormIsDirty(props.dirty), 1);
                }}
              />
            </FormTab>
            <ViewTab label="Related Investors">
              <OrganizationRelatedInvestorsLabel label="Related Investors" />
              <List
                style={{ margin: '0px', width: '100%' }}
                filters={<InvestorFilters />}
                filter={{ organizationId }}
                resource="investors"
                basePath="/investors"
                sort={{ field: 'name', order: 'ASC' }}
                pagination={<ListPagination />}
                title=" "
                actions={
                  <OrganizationInvestorAction
                    organizationId={organizationId}
                    handleInvestorCreateButtonClick={
                      this.handleOrganizationInvestorCreateDialogToggle
                    }
                    handleAddExistingInvestorCreateDialogToggle={
                      this.handleAddExistingInvestorCreateDialogToggle
                    }
                    hasCreateInvestorPermission={hasCreateInvestorPermission}
                    hasEditInvestorPermission={hasEditInvestorPermission}
                    hasExportOrganizationRelatedInvestorPermission={
                      hasExportOrganizationRelatedInvestorPermission
                    }
                  />
                }
                bulkActionButtons={
                  hasEditInvestorPermission && (
                    <RemoveOrganizationInvestorButton {...rest} />
                  )
                }
                empty={
                  <Empty
                    emptyText="There are no related Investors to be displayed"
                    buttons={[
                      {
                        label: 'Add New Investor',
                        onClick: this
                          .handleOrganizationInvestorCreateDialogToggle,
                        hide: !hasCreateInvestorPermission
                      },
                      {
                        label: 'Add Existing Investor',
                        onClick: this
                          .handleAddExistingInvestorCreateDialogToggle,
                        hide: !hasEditInvestorPermission
                      }
                    ]}
                  />
                }
                hasCreate={false}
              >
                <OrganizationInvestorDatagrid
                  permissions={permissions}
                  {...rest}
                />
              </List>
              <OrganizationInvestorCreateDialog
                title=" "
                showDialog={showOrganizationInvestorCreateDialog}
                handleDialogToggle={
                  this.handleOrganizationInvestorCreateDialogToggle
                }
                {...rest}
              />
              <AddExistingInvestorDialog
                title=" "
                showDialog={showAddExistingInvestorCreateDialog}
                handleDialogToggle={
                  this.handleAddExistingInvestorCreateDialogToggle
                }
                organizationId={organizationId}
                {...rest}
              />
            </ViewTab>
          </TabbedForm>
        </Edit>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state, props) => ({
  investors: state.admin.resources.investors // List of investors
});

export default connect(
  mapStateToProps,
  null
)(withStyles(styles)(OrganizationEdit));
